import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Metadata from '../../components/Metadata/Metadata';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Section from '../../components/Section/Section';
import Accordion from '../../components/Accordion/Accordion';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import Button from '../../components/Button/Button';
import Leadership from '../../components/Leadership/Leadership';

import sbagrowth from '../../assets/images/objects/sba-growth-award-2024.svg';
import sbacert from '../../assets/images/objects/sba-certified.svg';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };
  return (
    <Box>
      <Metadata
        title="About Us | General Genomics"
        description="Discover the history, mission, and vision of General Genomics. Learn about our leadership, core values, and commitment to innovation in bioscience and AI, driving transformative solutions in the industry."
      />
      <Hero
        helper="About Us"
        headline="General Genomics: Our journey and purpose"
      />

      <Section>
        <ContentSplit
          image="/images/library/handshake-military-civilian-3.webp"
          position="left"
        >
          <h2 className="action-on-view action-fade-in">
            Our Mission and Vision
          </h2>
          <p className="action-on-view action-fade-in">
            General Genomics was founded on the belief that data has the power
            to solve humanity’s greatest challenges. As a Service-Disabled
            Veteran-Owned Small Business, our mission is rooted in national
            security and the advancement of groundbreaking AI and ML solutions.
            From enhancing military operations to streamlining healthcare
            logistics, we are dedicated to creating innovative systems that
            improve efficiency, reduce risks, and support our clients’ critical
            missions.
          </p>
        </ContentSplit>
      </Section>

      <Section>
        <ContentSplit image="/images/library/gg46.webp" position="right">
          <h2 className="action-on-view action-fade-in">
            Curo46™: A Revolution in AI
          </h2>
          <p className="action-on-view action-fade-in">
            Our flagship platform, Curo46™, stands at the intersection of
            biology and technology. Designed to analyze both biological and
            man-made systems, Curo46™ harnesses proprietary data and LLMs to
            deliver actionable insights. It empowers industries with predictive
            health risk management, supply chain optimization, and advanced
            cybersecurity measures, setting a new standard in AI-driven
            innovation.
          </p>
        </ContentSplit>
      </Section>

      <Section>
        <ContentSplit image="/images/library/data-nodex-1.webp" position="left">
          <h2 className="action-on-view action-fade-in">General Genomics AI</h2>
          <p className="action-on-view action-fade-in">
            General Genomics AI (GGAI) delivers cutting-edge AI solutions and
            advanced EdTech services that empower organizations to achieve
            transformative outcomes. Specializing in federal and state
            government contracts, GGAI also supports commercial clients in the
            medical research, bioscience, and bioinformatics sectors. By
            leveraging data-driven innovation, we enable our partners to
            navigate complex challenges with precision and efficiency. Located
            in Fort Worth, TX, GGAI is a wholly owned subsidiary of General
            Genomics Inc., dedicated to driving progress through intelligent
            technology.
          </p>
        </ContentSplit>
      </Section>

      <Section>
        <ContentSplit image="/images/library/soldier-ai.webp" position="right">
          <h2 className="action-on-view action-fade-in">
            AI-Driven National Security
          </h2>
          <p className="action-on-view action-fade-in">
            At the heart of our operations is a commitment to safeguarding the
            nation. General Genomics develops cutting-edge AI and ML
            technologies to support joint forces, enabling decisive action
            against emerging threats. From counter-drone capabilities to human
            performance optimization, our solutions are crafted to ensure
            mission readiness and operational superiority.
          </p>
        </ContentSplit>
      </Section>

      <Section>
        <ContentSplit image="/images/library/data-nodes-4.webp" position="left">
          <h2 className="action-on-view action-fade-in">
            Health Informatics and Predictive Analytics
          </h2>
          <p className="action-on-view action-fade-in">
            Beyond national defense, General Genomics is transforming healthcare
            through AI-driven precision medicine, remote health monitoring, and
            predictive analytics. Our solutions empower medical professionals to
            deliver better outcomes by optimizing workflows, enhancing
            decision-making, and advancing genomic research. We&apos;re not just
            innovating — we&apos;re helping to heal the world through objective
            data science.
          </p>
        </ContentSplit>
      </Section>

      <Section theme="dark">
        <h2 className="action-on-view action-fade-in">
          General Genomics Leadership Team
        </h2>
        <p className="action-on-view action-fade-in">Meet the team...</p>
        <Leadership />
      </Section>

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Proudly Recognized by the U.S. Small Business Administration
        </h2>
        <p className="action-on-view action-fade-in">
          Certified as a Service-Disabled Veteran-Owned Business and honored as
          a 2024 Growth Accelerator Fund Competition Awardee.
        </p>
        <div className="card-group card-max-row-4">
          <Card iconImage={sbacert} className="action-on-view action-fade-in" />
          <Card
            iconImage={sbagrowth}
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Dive deeper into our work, opportunities, and resources to learn how
          General Genomics is shaping the future of healthcare.
        </p>
        <div className="card-group">
          <Card
            icon="work"
            headline="Careers"
            subhead="Join our dynamic team at General Genomics and contribute to groundbreaking advancements in genomics and healthcare."
            btnURL="/about-us/careers"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="trending_up"
            headline="Our Approach"
            subhead="Discover how General Genomics delivers on its mission through cutting-edge solutions and innovation in genomics."
            btnURL="/our-approach"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="business"
            headline="Industries"
            subhead="Explore the industries we serve and see how our expertise transforms genomics into actionable solutions."
            btnURL="/industries"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="email"
            headline="Contact Us"
            subhead="Have questions or want to connect? Reach out to our team and start a conversation today!"
            btnURL="/contact-us"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/office-group-4.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics business and
          offerings
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn about General Genomics&apos; history, mission, leadership, core
          values, and dedication to bioscience and AI innovation.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What is General Genomics' mission and vision?"
          >
            General Genomics is dedicated to advancing bioscience and AI to
            deliver innovative and transformative solutions, improving global
            healthcare.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How has General Genomics evolved over the years?"
          >
            Founded with core principles rooted in innovation and excellence,
            the company has grown into a leader in bioscience and AI,
            continually pushing technological boundaries.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Who are the key leaders at General Genomics?"
          >
            Our leadership team brings deep expertise and a shared commitment to
            our mission. Learn more about their profiles on our Leadership Team
            page.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What makes General Genomics stand out in the bioscience and AI industries?"
          >
            Our commitment to continuous innovation, excellence, and practical
            solutions sets us apart, helping advance cutting-edge technologies
            in healthcare and beyond.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more about General Genomics and get in touch?"
          >
            Visit our <Link to="/contact-us">Contact Us</Link> page for more
            information or to reach out for specific inquiries about our
            offerings, services, and collaborations.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
